import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { DeliveryQuote } from '../../types/aai';

/*
 * The Delivery Project quote.
 *
 * A quote is what a client said yes to, so it is always a specific version.
 * Accepting one supersedes the last, and an accepted version cannot be edited —
 * changing the price of something already agreed would rewrite the agreement
 * rather than propose a new one. The card says which version delivery is
 * working to on its face, because that is the question it exists to answer.
 */

const STATE_TONE: Record<string, string> = {
  accepted: 'bg-ok-soft text-[#3F7A2F]',
  draft: 'bg-canvas-inset text-ink-muted',
  sent: 'bg-[rgba(255,234,0,0.2)] text-[#8A6D00]',
  superseded: 'bg-canvas-inset text-ink-soft',
  declined: 'bg-danger-soft text-danger',
};

const STATE_LABEL: Record<string, string> = {
  accepted: 'Accepted',
  draft: 'Draft',
  sent: 'With the client',
  superseded: 'Superseded',
  declined: 'Declined',
};

const CONFIDENCE = ['Low', 'Medium', 'High'];

// Accepting is the client Owner's decision, never the partner's. The card says
// where the version stands and what the partner can still do about it.
const FOOT: Record<string, string> = {
  draft: 'This version is a draft only you can see. Send it to put it in front of the client Owner.',
  sent: 'With the client. The Owner accepts it from their Project record; delivery works to it once they do.',
  accepted: 'Delivery uses this version. Later changes are separate.',
  superseded: 'A later version replaced this one.',
  declined: 'The client declined this version. Start a new one to try again.',
};

const money = (amount: number | null, currency: string): string => {
  if (amount === null) return 'Not priced';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount}`;
  }
};

const listValue = (items: string[]): string => {
  if (items.length === 0) return 'Not recorded';
  return items.join(' · ');
};

const textValue = (value: string): string => {
  if (!value.trim()) return 'Not recorded';
  return value;
};

// Free text in, list out. A partner types a line, not a JSON array.
const toList = (raw: string): string[] =>
  raw.split('\n').map((line) => line.trim()).filter(Boolean);

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-card bg-canvas-inset px-4 py-3">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1 text-[13px] font-semibold text-ink">{value}</div>
  </div>
);

const HeadFact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="px-4 py-3">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1 text-[15px] font-bold text-ink">{value}</div>
  </div>
);

const INPUT = 'w-full rounded-card border border-line-strong bg-white px-3.5 py-2.5 text-[13.5px] text-ink outline-none focus:border-ink';

interface QuoteCardProps {
  requestId: string;
  quote: DeliveryQuote | null;
  quotes: DeliveryQuote[];
  onChanged: () => Promise<void>;
}

const QuoteCard: React.FC<QuoteCardProps> = ({ requestId, quote, quotes, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    price_amount: '',
    delivery_window: '',
    confidence: 'Medium',
    owner_name: '',
    expected_value: '',
    milestones: '',
    assumptions: '',
    exclusions: '',
    valid_until: '',
  });

  const startNew = () => {
    setForm({
      price_amount: `${quote?.price_amount ?? ''}`,
      delivery_window: quote?.delivery_window || '',
      confidence: quote?.confidence || 'Medium',
      owner_name: quote?.owner_name || '',
      expected_value: quote?.expected_value || '',
      milestones: (quote?.milestones || []).join('\n'),
      assumptions: (quote?.assumptions || []).join('\n'),
      exclusions: (quote?.exclusions || []).join('\n'),
      valid_until: quote?.valid_until || '',
    });
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const amount = form.price_amount.trim();
      await aaiApi.createDeliveryQuote(requestId, {
        price_amount: amount ? Number(amount) : undefined,
        delivery_window: form.delivery_window,
        confidence: form.confidence,
        owner_name: form.owner_name,
        expected_value: form.expected_value,
        milestones: toList(form.milestones),
        assumptions: toList(form.assumptions),
        exclusions: toList(form.exclusions),
        valid_until: form.valid_until,
      });
      await onChanged();
      setEditing(false);
      toast.success('New quote version saved as a draft.');
    } catch {
      toast.error('Could not save the quote.');
    } finally {
      setBusy(false);
    }
  };

  const send = async (target: DeliveryQuote) => {
    setBusy(true);
    try {
      await aaiApi.sendDeliveryQuote(requestId, target.id);
      await onChanged();
      toast.success('Sent. The client Owner can now accept this version.');
    } catch {
      toast.error('Could not send this version.');
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <section className="rounded-panel border-l-[3px] border-l-accent-500 bg-white p-5 md:p-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
          Delivery Project quote
        </div>
        <h3 className="m-0 mt-1.5 text-[17px] font-bold leading-tight text-ink">
          New version {(quotes[0]?.version || 0) + 1}
        </h3>
        <p className="m-0 mt-1.5 text-[12.5px] leading-[20px] text-ink-dim">
          Saved as a draft. Send it to the client when it is ready; it becomes the baseline delivery works to
          only when the client Owner accepts it, and the version it replaces is kept.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Price">
            <input
              type="number"
              min={0}
              value={form.price_amount}
              onChange={(e) => setForm({ ...form, price_amount: e.target.value })}
              placeholder="250000"
              className={INPUT}
            />
          </Field>
          <Field label="Delivery window">
            <input
              value={form.delivery_window}
              onChange={(e) => setForm({ ...form, delivery_window: e.target.value })}
              placeholder="18 weeks"
              className={INPUT}
            />
          </Field>
          <Field label="Confidence">
            <select
              value={form.confidence}
              onChange={(e) => setForm({ ...form, confidence: e.target.value })}
              className={INPUT}
            >
              {CONFIDENCE.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Owner">
            <input
              value={form.owner_name}
              onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
              placeholder="Theo Martin"
              className={INPUT}
            />
          </Field>
          <Field label="Expected value" className="sm:col-span-2">
            <input
              value={form.expected_value}
              onChange={(e) => setForm({ ...form, expected_value: e.target.value })}
              placeholder="$180k–$260k expected yearly value"
              className={INPUT}
            />
          </Field>
          <Field label="Milestones · one per line">
            <textarea
              rows={3}
              value={form.milestones}
              onChange={(e) => setForm({ ...form, milestones: e.target.value })}
              placeholder={'Definition\nBuild\nPilot and launch'}
              className={`${INPUT} resize-y`}
            />
          </Field>
          <Field label="Assumptions · one per line">
            <textarea
              rows={3}
              value={form.assumptions}
              onChange={(e) => setForm({ ...form, assumptions: e.target.value })}
              className={`${INPUT} resize-y`}
            />
          </Field>
          <Field label="Not included · one per line">
            <textarea
              rows={3}
              value={form.exclusions}
              onChange={(e) => setForm({ ...form, exclusions: e.target.value })}
              className={`${INPUT} resize-y`}
            />
          </Field>
          <Field label="Valid until">
            <input
              value={form.valid_until}
              onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              placeholder="Jul 8"
              className={INPUT}
            />
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="inline-flex h-11 items-center rounded-act bg-ink px-5 text-[13px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-50 whitespace-nowrap"
          >
            {busy && 'Saving…'}
            {!busy && 'Save version'}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="inline-flex h-11 items-center rounded-act border border-line-strong bg-white px-5 text-[13px] font-semibold text-ink transition-colors hover:bg-canvas-inset whitespace-nowrap"
          >
            Cancel
          </button>
        </div>
      </section>
    );
  }

  if (!quote) {
    return (
      <section className="rounded-panel border border-line bg-white p-5 md:p-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
          Delivery Project quote
        </div>
        <h3 className="m-0 mt-1.5 text-[17px] font-bold leading-tight text-ink">No quote prepared yet</h3>
        <p className="m-0 mt-1.5 text-[12.5px] leading-[20px] text-ink-dim">
          A Delivery Project has a commercial baseline: a price, a window, and what is and is not included.
          Nothing has been prepared for this one, so delivery has no agreed version to work to.
        </p>
        <button
          type="button"
          onClick={startNew}
          className="mt-4 inline-flex h-11 items-center rounded-act bg-ink px-5 text-[13px] font-semibold text-white transition-colors hover:bg-black whitespace-nowrap"
        >
          Prepare a quote
        </button>
      </section>
    );
  }

  const superseded = quotes.filter((q) => q.state === 'superseded').length;
  // The baseline is the accepted version, so a newer draft or sent version is
  // shown beneath it rather than hidden behind it: that is the version the
  // partner still has something to do about.
  const pending = quotes.find((q) => q.id !== quote.id && (q.state === 'draft' || q.state === 'sent')) || null;

  return (
    <section className="overflow-hidden rounded-panel border-l-[3px] border-l-accent-500 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 md:px-6">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
            Delivery Project quote
          </div>
          <h3 className="m-0 mt-1.5 text-[17px] font-bold leading-tight text-ink">
            {STATE_LABEL[quote.state] || quote.state} baseline · v{quote.version}
          </h3>
        </div>
        <span className={`inline-flex h-6 shrink-0 items-center rounded-md px-2.5 text-[11.5px] font-semibold ${STATE_TONE[quote.state]}`}>
          {STATE_LABEL[quote.state] || quote.state}
        </span>
      </div>

      <div className="mx-5 mt-4 grid divide-y divide-line rounded-card border border-line sm:grid-cols-4 sm:divide-x sm:divide-y-0 md:mx-6">
        <HeadFact label="Price" value={money(quote.price_amount, quote.price_currency)} />
        <HeadFact label="Delivery window" value={textValue(quote.delivery_window)} />
        <HeadFact label="Confidence" value={quote.confidence} />
        <HeadFact label="Owner" value={textValue(quote.owner_name)} />
      </div>

      <div className="grid gap-2.5 px-5 py-4 sm:grid-cols-2 md:px-6">
        <Tile label="Expected value" value={textValue(quote.expected_value)} />
        <Tile label="Milestones" value={listValue(quote.milestones)} />
        <Tile label="Assumptions" value={listValue(quote.assumptions)} />
        <Tile label="Not included" value={listValue(quote.exclusions)} />
        <Tile label="Valid until" value={textValue(quote.valid_until)} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-4 md:px-6">
        <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">
          {FOOT[quote.state] || FOOT.draft}
          {superseded > 0 && ` ${superseded} earlier version kept.`}
        </p>
        <div className="flex shrink-0 flex-wrap items-center gap-2.5">
          {quote.state === 'draft' && (
            <button
              type="button"
              onClick={() => send(quote)}
              disabled={busy}
              className="inline-flex h-11 items-center rounded-act bg-ink px-5 text-[13px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-50 whitespace-nowrap"
            >
              Send to client
            </button>
          )}
          <button
            type="button"
            onClick={startNew}
            disabled={busy}
            className="inline-flex h-11 items-center rounded-act border border-line-strong bg-white px-5 text-[13px] font-semibold text-ink transition-colors hover:bg-canvas-inset disabled:opacity-50 whitespace-nowrap"
          >
            New version
          </button>
        </div>
      </div>

      {pending && (
        <div className="border-t border-line bg-canvas-inset px-5 py-4 md:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="text-[13px] font-semibold text-ink">Version {pending.version}</span>
              <span className={`inline-flex h-6 items-center rounded-md px-2.5 text-[11.5px] font-semibold ${STATE_TONE[pending.state]}`}>
                {STATE_LABEL[pending.state]}
              </span>
              <span className="text-[13px] text-ink-dim">
                {money(pending.price_amount, pending.price_currency)} · {textValue(pending.delivery_window)} · {pending.confidence} confidence
              </span>
            </div>
            {pending.state === 'draft' && (
              <button
                type="button"
                onClick={() => send(pending)}
                disabled={busy}
                className="inline-flex h-11 items-center rounded-act bg-ink px-5 text-[13px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-50 whitespace-nowrap"
              >
                Send to client
              </button>
            )}
          </div>
          <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
            {FOOT[pending.state]} Accepting it replaces the v{quote.version} baseline above.
          </p>
        </div>
      )}
    </section>
  );
};

const Field: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({
  label, className = '', children,
}) => (
  <label className={`block ${className}`}>
    <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
      {label}
    </span>
    {children}
  </label>
);

export default QuoteCard;
